import { useRef, useState } from "react";
import { FileText, X } from "lucide-react";
import { cardMutedTextClass, cardSurfaceClass } from "@/components/common/surfaceClasses";
import type { OpsObligationAttachment } from "@/lib/opsObligationsClient";

const ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";
const IMAGE_MIMES = new Set(["image/jpeg", "image/png", "image/webp"]);

function truncate(s: string | null, n: number): string {
  if (!s) return "file";
  return s.length > n ? `${s.slice(0, n - 1)}\u2026` : s;
}

export function OpsObligationAttachmentStrip(props: {
  attachments: OpsObligationAttachment[];
  onAddFile: (file: File) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPickFile(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await props.onAddFile(file);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Documents</h3>
        <button
          type="button"
          className="rounded-md border border-gray-300 px-2 py-1 text-xs dark:border-zinc-600"
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          {busy ? "Uploading…" : "Attach file"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => void onPickFile(e)}
        />
      </div>
      {error ? <p className="text-xs text-red-700 dark:text-red-300">{error}</p> : null}
      {props.attachments.length === 0 ? (
        <p className={`text-xs ${cardMutedTextClass}`}>PDF or image of the contract / bill.</p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {props.attachments.map((a) => {
            const image = IMAGE_MIMES.has(a.content_type);
            const href = a.signed_url;
            return (
              <li key={a.id} className={`relative ${cardSurfaceClass} p-1`}>
                {href ? (
                  <a href={href} target="_blank" rel="noreferrer" className="block">
                    {image ? (
                      <img src={href} alt={a.display_name ?? "attachment"} className="h-14 w-14 rounded object-cover" />
                    ) : (
                      <span className="flex h-14 w-14 flex-col items-center justify-center gap-1 text-[10px]">
                        <FileText className="h-5 w-5" />
                        {truncate(a.display_name, 8)}
                      </span>
                    )}
                  </a>
                ) : (
                  <span className="flex h-14 w-14 items-center justify-center text-[10px]">
                    {truncate(a.display_name, 8)}
                  </span>
                )}
                <button
                  type="button"
                  className="absolute -right-1 -top-1 rounded-full bg-white p-0.5 text-gray-600 shadow dark:bg-zinc-800"
                  aria-label="Remove attachment"
                  onClick={() => void props.onRemove(a.id)}
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
